import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, ChevronLeft, ChevronRight, Plus, RefreshCw } from "lucide-react";
import {
  cancelAppointment,
  completeAppointment,
  createAppointment,
  getAvailableSlots,
  getSchedulingErrorMessage,
  listAppointments,
  markNoShow,
  searchCustomers,
  updateAppointment,
} from "../../../api/schedulingApi";
import {
  Alert,
  Field,
  Modal,
  StatusPill,
  addDays,
  btnDanger,
  btnPrimary,
  btnSecondary,
  card,
  formatDate,
  formatTime,
  inputClass,
  localParts,
  todayIn,
} from "./ui";

function SlotPicker({ botId, timeZone, serviceId, providerId, date, value, onChange }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!providerId || !date) {
      setSlots([]);
      return;
    }
    let active = true;
    setLoading(true);
    getAvailableSlots(botId, { date, provider_id: providerId, service_id: serviceId || undefined })
      .then((data) => active && setSlots(data || []))
      .catch(() => active && setSlots([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [botId, serviceId, providerId, date]);

  if (!providerId || !date) return <p className="text-xs text-slate-400">Choose a provider and date to see free slots.</p>;
  if (loading) return <p className="text-xs text-slate-500">Loading free slots…</p>;
  if (!slots.length) return <p className="text-xs text-amber-700">No free slots on this day. Use a custom time if needed.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {slots.map((s) => (
        <button
          key={s.start_at}
          type="button"
          onClick={() => onChange(s.start_at)}
          className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
            value === s.start_at ? "border-[#096dea] bg-[#096dea] text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"
          }`}
        >
          {formatTime(s.start_at, timeZone)}
        </button>
      ))}
    </div>
  );
}

function TimeChooser({ botId, timeZone, providers, services, initial, showService, onSubmit, submitLabel, children }) {
  const [serviceId, setServiceId] = useState(initial.service_id || "");
  const [providerId, setProviderId] = useState(initial.provider_id || "");
  const [date, setDate] = useState(initial.date);
  const [slot, setSlot] = useState("");
  const [custom, setCustom] = useState(false);
  const [customTime, setCustomTime] = useState(initial.time || "10:00");

  const startAt = custom ? (date && customTime ? `${date}T${customTime}:00` : "") : slot;
  const activeProviders = providers.filter((p) => p.is_active || String(p.id) === String(initial.provider_id));

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          service_id: serviceId ? Number(serviceId) : null,
          provider_id: Number(providerId),
          start_at: startAt,
          override_schedule: custom,
        });
      }}
    >
      {showService && services.length > 0 && (
        <Field label="Service" hint="Optional. Sets the appointment length.">
          <select className={inputClass} value={serviceId} onChange={(e) => { setServiceId(e.target.value); setSlot(""); }}>
            <option value="">No specific service</option>
            {services.filter((s) => s.is_active).map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes} min)</option>
            ))}
          </select>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Provider">
          <select required className={inputClass} value={providerId} onChange={(e) => { setProviderId(e.target.value); setSlot(""); }}>
            <option value="">Select…</option>
            {activeProviders.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input required type="date" className={inputClass} value={date} onChange={(e) => { setDate(e.target.value); setSlot(""); }} />
        </Field>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-600">Time</span>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={custom} onChange={(e) => setCustom(e.target.checked)} />
            Custom time (outside working hours)
          </label>
        </div>
        {custom ? (
          <input type="time" required className={inputClass} value={customTime} onChange={(e) => setCustomTime(e.target.value)} />
        ) : (
          <SlotPicker botId={botId} timeZone={timeZone} serviceId={serviceId} providerId={providerId} date={date} value={slot} onChange={setSlot} />
        )}
      </div>
      {children}
      <div className="flex justify-end">
        <button type="submit" className={btnPrimary} disabled={!providerId || !startAt}>{submitLabel}</button>
      </div>
    </form>
  );
}

function NewAppointmentModal({ open, onClose, botId, timeZone, providers, services, defaultDate, onCreated }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [matches, setMatches] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPhone(""); setName(""); setNotes(""); setMatches([]); setCustomerId(null); setError("");
  }, [open]);

  useEffect(() => {
    if (phone.trim().length < 6) {
      setMatches([]);
      return;
    }
    const t = setTimeout(() => {
      searchCustomers(botId, { phone: phone.trim() }).then((d) => setMatches(d || [])).catch(() => setMatches([]));
    }, 300);
    return () => clearTimeout(t);
  }, [botId, phone]);

  const submit = async (timeValues) => {
    setError("");
    try {
      await createAppointment(botId, {
        ...timeValues,
        source: "admin",
        notes: notes.trim() || null,
        ...(customerId ? { customer_id: customerId } : { customer: { phone: phone.trim() || null, name: name.trim() || null } }),
      });
      onCreated();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not create the appointment."));
    }
  };

  return (
    <Modal open={open} title="New appointment" onClose={onClose} wide>
      <Alert>{error}</Alert>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Phone (with country code)">
          <input className={inputClass} value={phone} onChange={(e) => { setPhone(e.target.value); setCustomerId(null); }} placeholder="9198xxxxxxxx" />
        </Field>
        <Field label="Name">
          <input className={inputClass} value={name} onChange={(e) => { setName(e.target.value); setCustomerId(null); }} required={!customerId} />
        </Field>
      </div>
      {matches.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">Existing:</span>
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setCustomerId(c.id); setName(c.name || ""); }}
              className={`rounded-lg border px-2 py-1 ${customerId === c.id ? "border-[#096dea] bg-blue-50 text-[#096dea]" : "border-slate-200 text-slate-700"}`}
            >
              {c.name || "Unnamed"}
            </button>
          ))}
        </div>
      )}
      <TimeChooser
        botId={botId}
        timeZone={timeZone}
        providers={providers}
        services={services}
        showService
        initial={{ date: defaultDate }}
        submitLabel="Book appointment"
        onSubmit={submit}
      >
        <Field label="Notes">
          <textarea rows={2} className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </TimeChooser>
    </Modal>
  );
}

function RescheduleModal({ appointment, onClose, botId, timeZone, providers, services, onSaved }) {
  const [error, setError] = useState("");
  useEffect(() => setError(""), [appointment]);
  if (!appointment) return null;
  const local = localParts(appointment.start_at, timeZone);

  const submit = async ({ provider_id, start_at }) => {
    setError("");
    try {
      await updateAppointment(botId, appointment.id, { provider_id, start_at });
      onSaved();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not reschedule."));
    }
  };

  return (
    <Modal open title={`Reschedule · ${appointment.customer_name || "Customer"}`} onClose={onClose} wide>
      <Alert>{error}</Alert>
      <TimeChooser
        botId={botId}
        timeZone={timeZone}
        providers={providers}
        services={services}
        initial={{ provider_id: appointment.provider_id, service_id: appointment.service_id, date: local.date, time: local.time }}
        submitLabel="Save new time"
        onSubmit={submit}
      />
    </Modal>
  );
}

function CancelModal({ appointment, onClose, botId, onSaved }) {
  const [by, setBy] = useState("admin");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setBy("admin"); setReason(""); setError(""); }, [appointment]);
  if (!appointment) return null;

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await cancelAppointment(botId, appointment.id, { cancelled_by: by, cancel_reason: reason.trim() || null });
      onSaved();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not cancel."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Cancel appointment"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={btnSecondary} onClick={onClose}>Keep</button>
          <button type="button" className={btnDanger} onClick={submit} disabled={saving}>{saving ? "Cancelling…" : "Cancel appointment"}</button>
        </>
      }
    >
      <Alert>{error}</Alert>
      <Field label="Cancelled by">
        <select className={inputClass} value={by} onChange={(e) => setBy(e.target.value)}>
          <option value="admin">Staff</option>
          <option value="provider">Provider</option>
          <option value="customer">Customer</option>
        </select>
      </Field>
      <Field label="Reason">
        <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Doctor on emergency leave" />
      </Field>
    </Modal>
  );
}

export default function AppointmentsPanel({ botId, timeZone, providers, services }) {
  const [date, setDate] = useState(() => todayIn(timeZone));
  const [providerId, setProviderId] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [rescheduling, setRescheduling] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const providerName = (id) => providers.find((p) => p.id === id)?.name || `#${id}`;
  const serviceName = (id) => services.find((s) => s.id === id)?.name;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listAppointments(botId, {
        date,
        provider_id: providerId || undefined,
        status: status || undefined,
      });
      setRows(data || []);
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not load appointments."));
    } finally {
      setLoading(false);
    }
  }, [botId, date, providerId, status]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setError("");
    try {
      await fn();
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err));
    }
  };

  const afterModal = () => {
    setShowNew(false);
    setRescheduling(null);
    setCancelling(null);
    load();
  };

  return (
    <div className={`${card} space-y-4`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <button type="button" className={btnSecondary} onClick={() => setDate((d) => addDays(d, -1))} aria-label="Previous day"><ChevronLeft size={16} /></button>
          <input type="date" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Date" />
          <button type="button" className={btnSecondary} onClick={() => setDate((d) => addDays(d, 1))} aria-label="Next day"><ChevronRight size={16} /></button>
          <button type="button" className={btnSecondary} onClick={() => setDate(todayIn(timeZone))}>Today</button>
          <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm" value={providerId} onChange={(e) => setProviderId(e.target.value)} aria-label="Provider filter">
            <option value="">All providers</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status filter">
            <option value="">All statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="no_show">No-show</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button type="button" className={btnSecondary} onClick={load} aria-label="Refresh"><RefreshCw size={16} /></button>
        </div>
        <button type="button" className={btnPrimary} onClick={() => setShowNew(true)} disabled={!providers.some((p) => p.is_active)}>
          <Plus size={16} /> New appointment
        </button>
      </div>

      <p className="text-xs text-slate-500">{formatDate(date)} · times shown in {timeZone}</p>
      <Alert>{error}</Alert>
      {!providers.length && <Alert kind="info">Add a provider and working hours first (Providers tab).</Alert>}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
          <CalendarClock size={28} />
          <p className="text-sm">No appointments on this day.</p>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((a) => (
            <li key={a.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="flex gap-4">
                <div className="w-24 shrink-0 text-sm font-bold text-slate-900">
                  {formatTime(a.start_at, timeZone)}
                  <div className="text-xs font-normal text-slate-400">to {formatTime(a.end_at, timeZone)}</div>
                </div>
                <div className="space-y-0.5 text-sm">
                  <div className="font-semibold text-slate-900">
                    {a.customer_name || "Unnamed"} {a.customer_phone && <span className="font-normal text-slate-500">· {a.customer_phone}</span>}
                  </div>
                  <div className="text-xs text-slate-600">
                    {providerName(a.provider_id)}
                    {serviceName(a.service_id) && ` · ${serviceName(a.service_id)}`} · via {a.source}
                  </div>
                  {a.notes && <div className="text-xs text-slate-500">“{a.notes}”</div>}
                  {a.status === "cancelled" && a.cancel_reason && (
                    <div className="text-xs text-rose-600">Cancelled by {a.cancelled_by}: {a.cancel_reason}</div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill status={a.status} />
                {a.status === "confirmed" ? (
                  <>
                    <button type="button" className={btnSecondary} onClick={() => act(() => completeAppointment(botId, a.id))}>Done</button>
                    <button type="button" className={btnSecondary} onClick={() => act(() => markNoShow(botId, a.id))}>No-show</button>
                    <button type="button" className={btnSecondary} onClick={() => setRescheduling(a)}>Reschedule</button>
                    <button type="button" className={btnDanger} onClick={() => setCancelling(a)}>Cancel</button>
                  </>
                ) : (
                  <button type="button" className={btnSecondary} onClick={() => act(() => updateAppointment(botId, a.id, { status: "confirmed" }))}>
                    Restore
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <NewAppointmentModal
        open={showNew}
        onClose={() => setShowNew(false)}
        botId={botId}
        timeZone={timeZone}
        providers={providers}
        services={services}
        defaultDate={date}
        onCreated={afterModal}
      />
      <RescheduleModal appointment={rescheduling} onClose={() => setRescheduling(null)} botId={botId} timeZone={timeZone} providers={providers} services={services} onSaved={afterModal} />
      <CancelModal appointment={cancelling} onClose={() => setCancelling(null)} botId={botId} onSaved={afterModal} />
    </div>
  );
}
